import React from 'react';
import type { CouncilCost, SessionCouncilMetadata, EventLogEntry } from '../../../../types/council';
import { computeSpendDisplay } from '../../../trailhead/computeSpendDisplay';
import { maxCascadeDepth, mostConstrainedPeer, type CockpitRosterEntry } from './cockpitDerive';
import { HillChip } from '../../../trailhead/HillChip';
import { PosturePicker } from './PosturePicker';

export interface CockpitBandProps {
  sessionId: string;
  cost?: CouncilCost | null;
  roster: CockpitRosterEntry[];
  metadata: SessionCouncilMetadata;
  entries: EventLogEntry[];
  unread: number;
  posture: string;
  rolldownOpen: boolean;
  onToggleRolldown: () => void;
  darkMode: boolean;
  onSetPosture: (p: 'advisory' | 'gated' | 'autonomous') => void;
  posturePending: boolean;
  postureError: string | null;
  pickerOpen: boolean;
  setPickerOpen: (b: boolean) => void;
}

const POSTURE_STYLE: Record<string, string> = {
  advisory: 'text-gray-400', gated: 'text-amber-400', autonomous: 'text-emerald-400',
};

export const CockpitBand: React.FC<CockpitBandProps> = ({
  sessionId, cost, roster, metadata, entries, unread, posture, rolldownOpen, onToggleRolldown, darkMode,
  onSetPosture, posturePending, postureError, pickerOpen, setPickerOpen,
}) => {
  const spend = computeSpendDisplay(cost);
  const active = roster.filter(r => !r.dismissed_at);   // wire returns only active rows; defensive
  const depth = maxCascadeDepth(entries);
  const depthCap = metadata.autonomy_rules?.cascade_depth_cap ?? null;
  const tight = mostConstrainedPeer(active);
  const wrap = darkMode ? 'bg-gray-900/95 text-gray-200 border-gray-700' : 'bg-white/95 text-gray-700 border-gray-300';

  return (
    <div data-testid="cockpit-band"
      className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 rounded-md border shadow-md text-[11px] ${wrap}`}>
      {/* STATE — wraps internally so the ⛰ hill drops to its own line at the 420px
          panel instead of hard-clipping mid-glyph against the panel edge. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
        {spend && (
          <span className="inline-flex items-center gap-1 shrink-0">
            <span aria-hidden>💰</span><span className="tabular-nums">${spend.used.toFixed(2)} / ${spend.cap.toFixed(2)}</span>
            <span className={`inline-block h-1.5 w-12 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <span className={`block h-1.5 rounded ${spend.barColor}`} style={{ width: `${spend.frac * 100}%` }} />
            </span>
            {spend.breaches > 0 && <span className="font-bold text-red-500" data-testid="cockpit-breach">▲{spend.breaches}</span>}
          </span>
        )}
        <span data-testid="cockpit-peers" className="inline-flex items-center gap-1 shrink-0">
          <span aria-hidden>👥</span>{active.length}
          {tight && tight.loopFrac >= 0.8 && (
            <span className="text-amber-500" title="most-constrained peer">⚠</span>
          )}
        </span>
        <span data-testid="cockpit-depth" className="inline-flex items-center gap-1 shrink-0">
          <span aria-hidden>🌀</span>{depth}{depthCap != null ? `/${depthCap}` : ''}
        </span>
        {/* ⛰ Hill SETTER (first-class) — the existing HillChip relocated onto the band.
            Click → HillTargetCombobox (match-or-create Goal/Project/Task or free-text).
            Capped at 120px so HillChip's own max-w-[280px] can't over-reserve at 420px. */}
        <span data-testid="cockpit-goal" className="min-w-0 max-w-[240px] truncate">
          <HillChip sessionId={sessionId} />
        </span>
      </div>
      {/* divider */}
      <span className={`h-4 border-l ${darkMode ? 'border-gray-700' : 'border-gray-300'} mx-1 shrink-0`} />
      {/* ACTION */}
      <div className="flex items-center gap-2 ml-auto shrink-0">
        <span className="opacity-40" title="cap nudge — Slice 4">💰[−][+]</span>
        <span className="opacity-40" title="pause — Slice 4">⏸</span>
        <span className="opacity-40" title="steer — Slice 5">🎯</span>
        <span className="relative inline-flex">
          <button type="button" data-testid="cockpit-posture" aria-haspopup="menu" aria-expanded={pickerOpen}
            onClick={() => setPickerOpen(!pickerOpen)}
            className={`uppercase tracking-wide ${POSTURE_STYLE[posture] ?? 'text-gray-400'}`}
            title="governor posture — click to change">⚖ {posture}</button>
          {pickerOpen && (
            <PosturePicker current={posture} darkMode={darkMode} pending={posturePending} error={postureError}
              onSelect={onSetPosture}
              onClose={() => setPickerOpen(false)} />
          )}
        </span>
        <button type="button" data-testid="cockpit-rolldown-toggle" onClick={onToggleRolldown}
          className="inline-flex items-center gap-1 hover:opacity-80">
          📜 {unread > 0 && <span className="text-red-500 font-bold">{unread}</span>}
          <span aria-hidden>{rolldownOpen ? '▾' : '▸'}</span>
        </button>
      </div>
    </div>
  );
};
